import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState, type ReactElement, type ReactNode } from 'react';

import { useListKeyboard } from './keyboard.js';
import { paginate, sortRows, type SortDirection, type SortValue } from './logic.js';

/**
 * Tableau commun : en-têtes triables (`aria-sort`), pagination locale, état vide, et
 * présentation en cartes au téléphone (chaque cellule reprend son libellé). Les données
 * sont celles déjà chargées par l'écran : aucune requête supplémentaire.
 */

export interface Column<T> {
  key: string;
  header: string;
  /** Contenu de la cellule (sinon la valeur de tri, sinon rien). */
  render?: (row: T) => ReactNode;
  /** Valeur de tri ; sans elle, la colonne n'est pas triable. */
  sortValue?: (row: T) => SortValue;
  align?: 'start' | 'end' | 'center';
  /** Masquée au téléphone (information secondaire). */
  secondary?: boolean;
  /** Colonne principale : titre de la carte au téléphone. */
  primary?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  caption: string;
  /** Tri initial : clé de colonne et sens. */
  initialSort?: { key: string; direction: SortDirection };
  pageSize?: number;
  empty?: ReactNode;
  /** Ton de la ligne (bordure gauche) : urgence, retard… */
  rowTone?: (row: T) => 'ok' | 'warning' | 'critical' | undefined;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  caption,
  initialSort,
  pageSize = 25,
  empty,
  rowTone,
}: DataTableProps<T>): ReactElement {
  const [sort, setSort] = useState(initialSort);
  // Raccourcis des listes (/, j, k, Entrée) sur tous les tableaux de l'interface.
  const listRef = useListKeyboard();
  const [page, setPage] = useState(1);
  const sorted = useMemo(() => {
    const column = columns.find((c) => c.key === sort?.key);
    if (sort === undefined || column?.sortValue === undefined) return rows;
    return sortRows(rows, column.sortValue, sort.direction);
  }, [rows, columns, sort]);
  const slice = paginate(sorted, page, pageSize);

  if (rows.length === 0) return <div className="o-table-empty">{empty}</div>;

  const toggle = (key: string): void => {
    setPage(1);
    setSort((s) =>
      s?.key === key
        ? { key, direction: s.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
  };

  return (
    <div className="o-datatable" ref={listRef}>
      <div className="o-table-wrap">
        <table className="o-table o-table--responsive">
          <caption className="o-visually-hidden">{caption}</caption>
          <thead>
            <tr>
              {columns.map((c) => {
                const active = sort?.key === c.key;
                const ariaSort = active
                  ? sort.direction === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : undefined;
                const Icon = active
                  ? sort.direction === 'asc'
                    ? ArrowUp
                    : ArrowDown
                  : ArrowUpDown;
                return (
                  <th
                    key={c.key}
                    scope="col"
                    aria-sort={ariaSort}
                    className={c.align === undefined ? undefined : `o-align-${c.align}`}
                  >
                    {c.sortValue === undefined ? (
                      c.header
                    ) : (
                      <button
                        type="button"
                        className="o-sort"
                        onClick={() => {
                          toggle(c.key);
                        }}
                      >
                        {c.header}
                        <Icon aria-hidden="true" size={14} />
                      </button>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {slice.items.map((row) => {
              const tone = rowTone?.(row);
              return (
                <tr key={rowKey(row)} className={tone === undefined ? undefined : `o-row--${tone}`}>
                  {columns.map((c) => {
                    const classes = [
                      c.align === undefined ? '' : `o-align-${c.align}`,
                      c.secondary === true ? 'o-cell--secondary' : '',
                      c.primary === true ? 'o-cell--primary' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    const value = c.render?.(row) ?? (c.sortValue?.(row) as ReactNode) ?? '—';
                    return (
                      <td
                        key={c.key}
                        data-label={c.header}
                        className={classes === '' ? undefined : classes}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {slice.pageCount > 1 && (
        <Pagination
          page={slice.page}
          pageCount={slice.pageCount}
          from={slice.from}
          to={slice.to}
          total={slice.total}
          onPage={setPage}
        />
      )}
    </div>
  );
}

export function Pagination({
  page,
  pageCount,
  from,
  to,
  total,
  onPage,
}: {
  page: number;
  pageCount: number;
  from: number;
  to: number;
  total: number;
  onPage: (page: number) => void;
}): ReactElement {
  return (
    <nav className="o-pagination" aria-label="Pagination">
      <span className="o-hint" aria-live="polite">
        {from}–{to} sur {total}
      </span>
      <div className="o-row">
        <button
          type="button"
          className="o-button o-button--small"
          disabled={page <= 1}
          onClick={() => {
            onPage(page - 1);
          }}
        >
          <ChevronLeft aria-hidden="true" size={16} /> Précédente
        </button>
        <span className="o-hint">
          Page {page} / {pageCount}
        </span>
        <button
          type="button"
          className="o-button o-button--small"
          disabled={page >= pageCount}
          onClick={() => {
            onPage(page + 1);
          }}
        >
          Suivante <ChevronRight aria-hidden="true" size={16} />
        </button>
      </div>
    </nav>
  );
}
